/**
 * AcknowledgeAssignment — the manager's "Acknowledge" action on a learner's
 * COMPLETED training assignment (training dashboard assignment row).
 *
 * Shown only when Central grants `org:admin` in the current workspace; the
 * server enforces the same rule and records the endorser's role from Central.
 * An existing live acknowledgement by this manager shows as "Acknowledged"
 * with a revoke action (a reason is required; history is kept).
 */

import { useState } from 'react';
import {
  ENDORSEMENT_STATEMENT_MAX,
  useCreateEndorsementMutation,
  useRevokeEndorsementMutation,
  type Endorsement,
} from '../../store/api/endorsementsApi';

interface Props {
  assignmentId: string;
  learnerUserId: string;
  /** This manager's live (non-revoked) endorsement of this assignment, if any. */
  existing?: Endorsement;
}

function errorMessage(e: unknown): string {
  const data = (e as { data?: { error?: string; message?: string } })?.data;
  if (data?.error === 'ALREADY_ENDORSED') return 'You have already acknowledged this assignment.';
  if (data?.error === 'SELF_ENDORSEMENT') return 'You cannot acknowledge your own work.';
  return data?.message ?? 'Could not save. Please try again.';
}

export function AcknowledgeAssignment({ assignmentId, learnerUserId, existing }: Props) {
  const [mode, setMode] = useState<'idle' | 'endorse' | 'revoke'>('idle');
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [createEndorsement, { isLoading: creating }] = useCreateEndorsementMutation();
  const [revokeEndorsement, { isLoading: revoking }] = useRevokeEndorsementMutation();

  const reset = () => {
    setMode('idle');
    setText('');
    setError(null);
  };

  const submit = async () => {
    setError(null);
    try {
      if (mode === 'endorse') {
        await createEndorsement({
          learnerUserId,
          evidenceKind: 'training_assignment',
          evidenceId: assignmentId,
          statement: text.trim() || undefined,
        }).unwrap();
      } else if (mode === 'revoke' && existing) {
        await revokeEndorsement({ id: existing.id, reason: text.trim() }).unwrap();
      }
      reset();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  if (mode === 'idle') {
    return existing ? (
      <span className="inline-flex items-center gap-2 text-xs">
        <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-800">Acknowledged</span>
        <button type="button" className="text-gray-500 hover:text-red-600 underline" onClick={() => setMode('revoke')}>
          Revoke
        </button>
      </span>
    ) : (
      <button type="button" className="btn btn-sm btn-secondary" onClick={() => setMode('endorse')}>
        Acknowledge
      </button>
    );
  }

  const isRevoke = mode === 'revoke';
  return (
    <div className="flex flex-col gap-2 min-w-[16rem]">
      <textarea
        className="input text-sm"
        rows={2}
        maxLength={ENDORSEMENT_STATEMENT_MAX}
        placeholder={isRevoke ? 'Reason for revoking (required)' : 'Optional note about this work'}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={creating || revoking || (isRevoke && !text.trim())}
          onClick={submit}
        >
          {isRevoke ? 'Revoke' : 'Acknowledge'}
        </button>
        <button type="button" className="btn btn-sm btn-secondary" onClick={reset}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default AcknowledgeAssignment;
